<template>
  <div class="team-league-panel">
    <div v-if="loading" class="tlp-loading">
      <div class="spinner"></div>
      <p>Loading league data…</p>
    </div>

    <div v-else-if="!summary || !summary.mapped" class="tlp-empty">
      <p>This team isn't linked to a league yet.</p>
      <p class="tlp-empty__hint">
        An admin can link it on the
        <router-link to="/league-data">League Data</router-link> page.
      </p>
    </div>

    <template v-else>
      <div class="tlp-header">
        <h3>{{ summary.league_name }}</h3>
        <span class="tlp-header__season">Season {{ summary.season }}</span>
      </div>

      <!-- Summary strip -->
      <div class="tlp-summary">
        <div class="tlp-stat">
          <span class="tlp-stat__big">{{ ordinal(summary.ladder && summary.ladder.position) }}</span>
          <span class="tlp-stat__label">of {{ summary.ladder ? summary.ladder.total_teams : "–" }}</span>
        </div>
        <div class="tlp-stat">
          <span class="tlp-stat__big">{{ summary.ladder ? summary.ladder.points : "–" }}</span>
          <span class="tlp-stat__label">points</span>
        </div>
        <div v-if="summary.last_results.length" class="tlp-form">
          <span class="tlp-stat__label">Form</span>
          <span
            v-for="(r, i) in [...summary.last_results].reverse()"
            :key="i"
            class="tlp-form__badge"
            :class="'tlp-form__badge--' + r.outcome.toLowerCase()"
            :title="r.home_team + ' ' + r.home_score + '–' + r.away_score + ' ' + r.away_team"
          >
            {{ r.outcome }}
          </span>
        </div>
      </div>

      <!-- Next fixture card -->
      <div v-if="summary.next_fixture" class="tlp-next">
        <div class="tlp-next__label">Next game</div>
        <div class="tlp-next__teams">
          <span class="tlp-next__team">
            <img v-if="summary.next_fixture.home_logo" :src="summary.next_fixture.home_logo" alt="" />
            {{ summary.next_fixture.home_team }}
          </span>
          <span class="tlp-next__vs">v</span>
          <span class="tlp-next__team">
            <img v-if="summary.next_fixture.away_logo" :src="summary.next_fixture.away_logo" alt="" />
            {{ summary.next_fixture.away_team }}
          </span>
        </div>
        <div class="tlp-next__meta">
          {{ formatDate(summary.next_fixture.kickoff_utc) }},
          {{ formatKickoff(summary.next_fixture.kickoff_utc) }}
          <template v-if="summary.next_fixture.ground_name">
            · {{ summary.next_fixture.ground_name }}
          </template>
        </div>
      </div>

      <!-- Ladder -->
      <div class="tlp-section">
        <h4>Ladder</h4>
        <div class="tlp-table-scroll">
          <table class="tlp-table">
            <thead>
              <tr>
                <th>Pos</th>
                <th class="team-col">Team</th>
                <th>P</th>
                <th>W</th>
                <th>D</th>
                <th>L</th>
                <th>GD</th>
                <th>Pts</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in ladder"
                :key="row.external_team_id"
                :class="{ 'own-team': row.team_name === summary.external_team_name }"
              >
                <td>{{ row.position }}</td>
                <td class="team-col">
                  <img v-if="row.logo_url" :src="row.logo_url" alt="" class="tlp-logo" />
                  {{ row.team_name }}
                </td>
                <td>{{ row.played }}</td>
                <td>{{ row.won }}</td>
                <td>{{ row.drawn }}</td>
                <td>{{ row.lost }}</td>
                <td>{{ row.goal_difference }}</td>
                <td class="points">{{ row.points }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- This team's games -->
      <div class="tlp-section">
        <h4>Our games</h4>
        <div v-for="fx in teamFixtures" :key="fx.id" class="tlp-fixture">
          <span class="tlp-fixture__round">{{ fx.round_label }}</span>
          <span class="tlp-fixture__team tlp-fixture__team--home">
            {{ fx.home_team }}
            <img v-if="fx.home_logo" :src="fx.home_logo" alt="" class="tlp-logo" />
          </span>
          <span
            class="tlp-fixture__score"
            :class="{ 'tlp-fixture__score--time': fx.status !== 'complete' }"
          >
            <template v-if="fx.status === 'complete'">
              {{ fx.home_score }} – {{ fx.away_score }}
            </template>
            <template v-else>{{ formatKickoff(fx.kickoff_utc) }}</template>
          </span>
          <span class="tlp-fixture__team">
            <img v-if="fx.away_logo" :src="fx.away_logo" alt="" class="tlp-logo" />
            {{ fx.away_team }}
          </span>
          <span class="tlp-fixture__meta">
            {{ formatDate(fx.kickoff_utc) }}
            <template v-if="fx.status !== 'complete' && fx.ground_name">
              · {{ fx.ground_name }}
            </template>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ExternalLeagues } from "@/services/external-leagues-service.js";

// Kickoffs are stored UTC; display in the competition's local timezone.
const DISPLAY_TZ = "Australia/Adelaide";

export default {
  name: "TeamLeaguePanel",
  props: {
    teamId: { type: [Number, String], required: true },
    clubId: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      summary: null,
      ladder: [],
      fixtures: [],
    };
  },
  computed: {
    teamFixtures() {
      const us = this.summary?.external_team_name;
      if (!us) return [];
      return this.fixtures
        .filter((f) => f.home_team === us || f.away_team === us)
        .sort((a, b) => new Date(a.kickoff_utc) - new Date(b.kickoff_utc));
    },
  },
  watch: {
    teamId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.summary = null;
      this.ladder = [];
      this.fixtures = [];
      try {
        const res = await ExternalLeagues.getTeamSummary(this.clubId, this.teamId);
        this.summary = res.data || null;
        if (this.summary?.mapped) {
          const [ladderRes, fixturesRes] = await Promise.all([
            ExternalLeagues.getLadder(this.clubId, this.summary.league_id),
            ExternalLeagues.getFixtures(this.clubId, this.summary.league_id),
          ]);
          this.ladder = ladderRes.data || [];
          this.fixtures = fixturesRes.data || [];
        }
      } catch (e) {
        console.error("TeamLeaguePanel load error:", e);
        this.summary = null;
      } finally {
        this.loading = false;
      }
    },
    ordinal(n) {
      if (!n) return "–";
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    parseUtc(utc) {
      return new Date(utc.replace(" ", "T") + (utc.includes("Z") ? "" : "Z"));
    },
    formatDate(utc) {
      if (!utc) return "";
      return this.parseUtc(utc).toLocaleDateString("en-AU", {
        timeZone: DISPLAY_TZ,
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    },
    formatKickoff(utc) {
      if (!utc) return "";
      return this.parseUtc(utc).toLocaleTimeString("en-AU", {
        timeZone: DISPLAY_TZ,
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
};
</script>

<style scoped lang="scss">
.team-league-panel {
  padding: 4px 0;

  .tlp-loading,
  .tlp-empty {
    text-align: center;
    color: #6c757d;
    padding: 30px 0;
  }

  .tlp-empty__hint {
    font-size: 0.85rem;
  }

  .tlp-header {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 12px;

    h3 {
      margin: 0;
      font-size: 1.15rem;
    }

    &__season {
      color: #6c757d;
      font-size: 0.85rem;
    }
  }

  .tlp-summary {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    padding: 12px 16px;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    margin-bottom: 12px;
  }

  .tlp-stat {
    display: flex;
    align-items: baseline;
    gap: 6px;

    &__big {
      font-size: 1.5rem;
      font-weight: 700;
    }

    &__label {
      color: #6c757d;
      font-size: 0.8rem;
    }
  }

  .tlp-form {
    display: flex;
    align-items: center;
    gap: 5px;

    &__badge {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      font-weight: 700;
      color: #fff;

      &--w {
        background: #198754;
      }

      &--d {
        background: #adb5bd;
      }

      &--l {
        background: #dc3545;
      }
    }
  }

  .tlp-next {
    border: 1px solid #e9ecef;
    border-left: 4px solid #198754;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 20px;

    &__label {
      text-transform: uppercase;
      font-size: 0.7rem;
      letter-spacing: 0.5px;
      color: #6c757d;
      margin-bottom: 6px;
    }

    &__teams {
      display: flex;
      align-items: center;
      gap: 12px;
      font-weight: 600;
      flex-wrap: wrap;
    }

    &__team {
      display: flex;
      align-items: center;
      gap: 6px;

      img {
        width: 24px;
        height: 24px;
        object-fit: contain;
      }
    }

    &__vs {
      color: #6c757d;
      font-weight: 400;
    }

    &__meta {
      margin-top: 6px;
      color: #6c757d;
      font-size: 0.85rem;
    }
  }

  .tlp-section {
    margin-bottom: 24px;

    h4 {
      font-size: 1rem;
      margin-bottom: 10px;
      border-bottom: 2px solid #e9ecef;
      padding-bottom: 5px;
    }
  }

  .tlp-table-scroll {
    overflow-x: auto;
  }

  .tlp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;

    th,
    td {
      padding: 7px 9px;
      text-align: center;
      border-bottom: 1px solid #e9ecef;
      white-space: nowrap;
    }

    .team-col {
      text-align: left;
      min-width: 200px;
    }

    .points {
      font-weight: 700;
    }

    tr.own-team {
      background: #fff8e1;
      font-weight: 600;
    }
  }

  .tlp-logo {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  .tlp-fixture {
    display: grid;
    grid-template-columns: 40px 1fr 80px 1fr auto;
    align-items: center;
    gap: 8px;
    padding: 7px 4px;
    border-bottom: 1px solid #f1f3f5;
    font-size: 0.88rem;

    &__round {
      color: #6c757d;
      font-size: 0.78rem;
    }

    &__team {
      display: flex;
      align-items: center;
      gap: 6px;

      &--home {
        justify-content: flex-end;
        text-align: right;
      }
    }

    &__score {
      text-align: center;
      font-weight: 700;
      background: #f1f3f5;
      border-radius: 6px;
      padding: 3px 0;

      &--time {
        font-weight: 500;
        color: #495057;
      }
    }

    &__meta {
      color: #6c757d;
      font-size: 0.78rem;
      white-space: nowrap;
    }
  }

  @media (max-width: 640px) {
    .tlp-fixture {
      grid-template-columns: 34px 1fr 62px 1fr;

      &__meta {
        grid-column: 1 / -1;
        text-align: center;
      }
    }
  }
}
</style>
